import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerGovernanceApi } from '../../api/partner-governance-references-actions';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { IconActionButton } from '../../components/IconActionButton';
import { Check, UserX } from 'lucide-react';
import { useToast } from '../../components/Toast';

export function PartnerGovernanceTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const membersQuery = useQuery({ queryKey: ['partner-governance', partnerId], queryFn: () => partnerGovernanceApi.list(partnerId) });
  const { register, handleSubmit, reset, watch } = useForm<{ fullName: string; position: string; nationality: string; hasConflictOfInterest: boolean; conflictOfInterestDetails: string }>();
  const hasConflict = watch('hasConflictOfInterest');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-governance', partnerId] });
  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => partnerGovernanceApi.create(partnerId, values),
    onSuccess: () => { showSuccess('Board member added.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add board member.'),
  });
  const verifyMutation = useMutation({ mutationFn: (id: number) => partnerGovernanceApi.verify(id), onSuccess: () => { showSuccess('Verified.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const deactivateMutation = useMutation({ mutationFn: (id: number) => partnerGovernanceApi.deactivate(id), onSuccess: () => { showSuccess('Deactivated.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="panel">
      <div className="panel-body">
        {membersQuery.data && (
          <DataTable
            rows={membersQuery.data.data}
            rowKey={(m) => m.id}
            emptyMessage="No board/trustee members on file yet."
            columns={[
              { header: 'Name', render: (m) => m.full_name },
              { header: 'Position', render: (m) => m.position },
              { header: 'Nationality', render: (m) => m.nationality ?? '-' },
              { header: 'Conflict of Interest', render: (m) => Number(m.has_conflict_of_interest) === 1 ? (m.conflict_of_interest_details ?? 'Yes') : 'None declared' },
              { header: 'Status', render: (m) => m.verification_status === 'verified' ? 'Verified' : 'Unverified' },
              {
                header: '', render: (m) => (
                  <span className="row-actions">
                    {m.verification_status !== 'verified' && <IconActionButton icon={Check} label="Verify" onClick={() => verifyMutation.mutate(m.id)} />}
                    {Number(m.is_active) === 1 && <IconActionButton icon={UserX} label="Deactivate" danger onClick={() => deactivateMutation.mutate(m.id)} />}
                  </span>
                ),
              },
            ]}
          />
        )}

        <h3>Add Board/Trustee Member</h3>
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>Full Name<input {...register('fullName', { required: true })} /></label>
          <label>Position<input {...register('position', { required: true })} /></label>
          <label>Nationality<input {...register('nationality')} /></label>
          <label><input type="checkbox" {...register('hasConflictOfInterest')} /> Has Conflict of Interest</label>
          {hasConflict && <label>Details<input {...register('conflictOfInterestDetails')} /></label>}
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Adding…' : 'Add'}</button>
        </form>
      </div>
    </div>
  );
}
